import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Share2, Plus, X, Copy, Trash2, Loader2, Eye, ExternalLink,
  ShieldAlert, Clock,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { CLASSIFICATIONS, COMBINED_META, classTag } from "../lib/classification";
import { useAuth } from "../context/AuthContext";

/**
 * Investor share links — admin/manager panel for the Investor Dashboard.
 *
 * Mints signed, no-login URLs that render a redacted read-only investor
 * view (`/share/investor/:token`). Each link tracks scope (portfolio or a
 * single project), redaction mode (production-only vs full financials),
 * optional expiry and a running access counter for audit.
 */
export default function InvestorShares() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";

  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: pjs }] = await Promise.all([
        api.get("/investor-shares"),
        api.get("/projects"),
      ]);
      setRows(data || []);
      setProjects(pjs || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load share links");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (canManage) load(); }, [canManage]);

  const shareUrl = (token) => `${window.location.origin}/share/investor/${token}`;

  const copy = (token) => {
    navigator.clipboard.writeText(shareUrl(token))
      .then(() => toast.success("Share URL copied"))
      .catch(() => toast.error("Copy failed — select and copy manually"));
  };

  const revoke = async (row) => {
    if (!window.confirm(`Revoke this share link?\n\n"${row.label || row.id}" — ${row.access_count} view(s) so far. This action is immediate and permanent.`)) return;
    try {
      await api.delete(`/investor-shares/${row.id}`);
      toast.success("Share link revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Revoke failed");
    }
  };

  const activeCount = useMemo(() => rows.filter(r => r.active).length, [rows]);

  if (!canManage) return null;

  return (
    <section className="swiss-card-flat p-6 mb-6" data-testid="investor-shares-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Share2 size={14} style={{ color: "var(--accent-hover)" }} />
            <div className="label">Investor share links</div>
          </div>
          <p className="muted text-xs">
            Hand LPs a signed, no-login URL to view the portfolio or a single
            project. Pick production-only to redact financials, or full mirror
            for existing investors.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-[11px] muted">
            <div><span className="font-mono font-semibold text-[var(--ink)]">{activeCount}</span> active</div>
            <div><span className="font-mono font-semibold text-[var(--ink)]">{rows.length}</span> total</div>
          </div>
          <button className="btn-accent" onClick={() => setShowCreate(true)} data-testid="share-add-btn">
            <Plus size={13} /> Create share link
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center muted text-sm"><Loader2 size={14} className="animate-spin inline mr-1" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-center muted text-sm italic" data-testid="share-empty">
          No share links yet. Create one to expose a read-only investor view without a login.
        </div>
      ) : (
        <div className="border border-[var(--line)] rounded-md overflow-hidden">
          <table className="data-table w-full" data-testid="share-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Scope</th>
                <th>Mode</th>
                <th>Expires</th>
                <th className="text-right">Views</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const proj = r.scope === "project" && projects.find(p => p.id === r.project_id);
                return (
                  <tr key={r.id} data-testid={`share-row-${r.id}`}>
                    <td>
                      <div className="font-medium">{r.label || "Untitled"}</div>
                      <div className="font-mono text-[10px] muted truncate max-w-[280px]">
                        {shareUrl(r.token)}
                      </div>
                    </td>
                    <td className="text-xs">
                      {r.scope === "portfolio" ? "Portfolio" : (proj ? proj.name : "Project (deleted)")}
                    </td>
                    <td>
                      <span className={`tag text-[10px] ${r.mode === "full" ? "warn" : "ok"}`}>
                        {r.mode === "full" ? "Full mirror" : "Production only"}
                      </span>
                      {r.classification && r.classification !== "combined" && (() => {
                        const t = classTag(r.classification);
                        if (!t) return null;
                        return (
                          <span className="tag text-[10px] ml-1"
                                style={{ backgroundColor: t.bg, color: t.fg }}>
                            {t.label === "Development" ? "Dev" : t.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="font-mono text-xs muted">
                      {r.expires_at ? (r.expires_at.slice(0, 10)) : "never"}
                    </td>
                    <td className="text-right font-mono">{r.access_count}</td>
                    <td>
                      {r.active ? (
                        <span className="tag ok text-[10px]">active</span>
                      ) : (
                        <span className="tag danger text-[10px]">
                          {r.revoked_at ? "revoked" : "expired"}
                        </span>
                      )}
                    </td>
                    <td className="num">
                      <button onClick={() => copy(r.token)} className="mr-2" data-testid={`share-copy-${r.id}`} title="Copy URL">
                        <Copy size={13} className="muted" />
                      </button>
                      <a href={shareUrl(r.token)} target="_blank" rel="noreferrer" className="mr-2" data-testid={`share-open-${r.id}`} title="Preview">
                        <ExternalLink size={13} className="muted inline" />
                      </a>
                      {r.active && (
                        <button onClick={() => revoke(r)} data-testid={`share-revoke-${r.id}`} title="Revoke">
                          <Trash2 size={13} className="muted" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateShareModal
          projects={projects}
          onClose={() => setShowCreate(false)}
          onCreated={(newRow) => { setShowCreate(false); load(); copy(newRow.token); }}
        />
      )}
    </section>
  );
}


function CreateShareModal({ projects, onClose, onCreated }) {
  const [form, setForm] = useState({
    scope: "portfolio",
    project_id: "",
    mode: "production",
    classification: "combined",
    expires_choice: "168",  // 7 days
    label: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const expires_in_hours = form.expires_choice === "never" ? null : Number(form.expires_choice);
      const payload = {
        scope: form.scope,
        project_id: form.scope === "project" ? form.project_id : null,
        mode: form.mode,
        classification: form.classification,
        expires_in_hours,
        label: form.label || null,
      };
      if (form.scope === "project" && !form.project_id) {
        toast.error("Pick a project");
        setBusy(false); return;
      }
      const { data } = await api.post("/investor-shares", payload);
      toast.success("Share link created — URL copied to clipboard");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="share-create-modal">
      <form className="modal-card p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label">Create</div>
            <div className="font-display text-xl font-semibold mt-1">New investor share link</div>
          </div>
          <button type="button" onClick={onClose} className="muted"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="label block mb-1">Label (optional)</span>
            <input className="input-swiss" value={form.label} onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Q2 investor deck" data-testid="share-label" />
          </label>

          <div>
            <div className="label mb-2">Scope</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "portfolio", label: "Whole portfolio", desc: "Every project rolled up" },
                { v: "project", label: "Single project", desc: "One site only" },
              ].map(opt => (
                <button type="button" key={opt.v}
                  onClick={() => setForm(f => ({ ...f, scope: opt.v }))}
                  className={`p-3 rounded-md border text-left transition-colors ${form.scope === opt.v ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-[var(--line)]"}`}
                  data-testid={`share-scope-${opt.v}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: form.scope === opt.v ? "var(--accent-hover)" : "var(--ink-2)" }}>{opt.label}</div>
                  <div className="text-[11px] muted mt-1 leading-relaxed">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {form.scope === "project" && (
            <label className="block">
              <span className="label block mb-1">Project</span>
              <select className="input-swiss" value={form.project_id} onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))} required data-testid="share-project">
                <option value="">— pick a project —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          )}

          <div>
            <div className="label mb-2">Data mode</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "production", label: "Production only", desc: "kWp, generation, uptime — financials redacted" },
                { v: "full", label: "Full mirror", desc: "£, PPA rates, IRR & cashflow — full read-only" },
              ].map(opt => (
                <button type="button" key={opt.v}
                  onClick={() => setForm(f => ({ ...f, mode: opt.v }))}
                  className={`p-3 rounded-md border text-left transition-colors ${form.mode === opt.v ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-[var(--line)]"}`}
                  data-testid={`share-mode-${opt.v}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: form.mode === opt.v ? "var(--accent-hover)" : "var(--ink-2)" }}>
                    {opt.v === "production" ? <ShieldAlert size={12} /> : <Eye size={12} />}
                    {opt.label}
                  </div>
                  <div className="text-[11px] muted mt-1 leading-relaxed">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="label mb-2">Classification</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { v: "combined", label: COMBINED_META.label, desc: COMBINED_META.sub, tone: COMBINED_META.tone },
                ...CLASSIFICATIONS.map(c => ({ v: c.key, label: c.label, desc: c.sub, tone: c.tone })),
              ].map(opt => {
                const active = form.classification === opt.v;
                return (
                  <button type="button" key={opt.v}
                    onClick={() => setForm(f => ({ ...f, classification: opt.v }))}
                    className="p-2 rounded-md border text-left transition-colors"
                    style={active
                      ? { borderColor: opt.tone, background: `${opt.tone}15`, borderWidth: 2 }
                      : { borderColor: "var(--line)" }}
                    data-testid={`share-class-${opt.v}`}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider"
                         style={{ color: active ? opt.tone : "var(--ink-2)" }}>{opt.label}</div>
                    <div className="text-[10px] muted mt-0.5">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>


          <label className="block">
            <span className="label block mb-1 flex items-center gap-1"><Clock size={11} /> Expires</span>
            <select className="input-swiss" value={form.expires_choice} onChange={(e) => setForm(f => ({ ...f, expires_choice: e.target.value }))} data-testid="share-expiry">
              <option value="24">In 24 hours</option>
              <option value="168">In 7 days</option>
              <option value="720">In 30 days</option>
              <option value="never">Never (until revoked)</option>
            </select>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="share-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="btn-accent" data-testid="share-create-submit">
            {busy ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
            Create + copy URL
          </button>
        </div>
      </form>
    </div>
  );
}
